/**
 * Environment configuration for the Sound Anomalies frontend.
 * Loads configuration from Vite environment variables.
 */

interface Config {
  apiUrl: string;
  wsUrl: string;
  authSessionRetryMs: number;
  environment: "development" | "production" | "test";
  isDevelopment: boolean;
  isProduction: boolean;
}

const rawApiUrl = (
  import.meta.env.VITE_API_URL ||
  import.meta.env.VITE_API_BASE ||
  ""
).trim();
const browserOrigin =
  typeof window !== "undefined"
    ? window.location.origin.replace(/\/$/, "")
    : "";
const apiUsesSameOrigin = !rawApiUrl || rawApiUrl.toLowerCase() === "same-origin";
const normalizedApiUrl = rawApiUrl.replace(/\/$/, "");
const resolvedApiUrl = apiUsesSameOrigin ? browserOrigin : normalizedApiUrl;

if (!resolvedApiUrl) {
  throw new Error(
    "VITE_API_URL or VITE_API_BASE is required outside development defaults",
  );
}
const parsedApiUrl = new URL(resolvedApiUrl);
if (
  parsedApiUrl.pathname !== "/" ||
  parsedApiUrl.search !== "" ||
  parsedApiUrl.hash !== ""
) {
  throw new Error("VITE_API_URL must be an API origin without a path or query");
}
if (import.meta.env.PROD && parsedApiUrl.protocol !== "https:") {
  throw new Error("Production builds must set VITE_API_URL to an HTTPS origin");
}
const rawWsUrl = (import.meta.env.VITE_WS_URL || "").trim();
const defaultWsUrl = resolvedApiUrl.replace(/^http/i, "ws");
const wsUsesSameOrigin = rawWsUrl.toLowerCase() === "same-origin";
const normalizedWsUrl = wsUsesSameOrigin
  ? resolvedApiUrl.replace(/^http/i, "ws")
  : rawWsUrl.replace(/\/$/, "");
const resolvedWsUrl = (normalizedWsUrl || defaultWsUrl).replace(/\/$/, "");
const DEFAULT_AUTH_SESSION_RETRY_MS = 10_000;
const rawAuthSessionRetryMs = import.meta.env.VITE_AUTH_SESSION_RETRY_MS;
const authSessionRetryMs = rawAuthSessionRetryMs
  ? Number(rawAuthSessionRetryMs)
  : DEFAULT_AUTH_SESSION_RETRY_MS;

if (!Number.isSafeInteger(authSessionRetryMs) || authSessionRetryMs <= 0) {
  throw new Error("VITE_AUTH_SESSION_RETRY_MS must be a positive integer");
}

if (!resolvedWsUrl) {
  throw new Error("VITE_WS_URL is required outside development defaults");
}
if (
  import.meta.env.PROD &&
  !wsUsesSameOrigin &&
  /^ws?:\/\/(localhost|127\.0\.0\.1)([:/]|$)/i.test(resolvedWsUrl)
) {
  throw new Error(
    "Production builds must set VITE_WS_URL to a public WSS endpoint (no localhost)",
  );
}

export const config: Config = {
  apiUrl: resolvedApiUrl,
  wsUrl: resolvedWsUrl,
  authSessionRetryMs,
  environment:
    (import.meta.env.VITE_ENVIRONMENT as Config["environment"]) ||
    "development",
  isDevelopment: import.meta.env.DEV,
  isProduction: import.meta.env.PROD,
};

export default config;
